import type { ComponentProps, ReactNode } from "react"
import { useId } from "react"
import FieldDescriptionOrError from "./FieldDescriptionOrError"
import { Field, FieldLabel } from "@/ui/components/ui/field"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/ui/components/ui/input-group"
import { cn } from "@/ui/lib/utils"

type Props = ComponentProps<typeof InputGroupInput> &
  Pick<ComponentProps<typeof Field>, "orientation"> & {
    label?: string
    inputClassName?: string
    icon?: ReactNode
    error?: string | null
    helperText?: string
  }

export default function InputField({
  label,
  inputClassName,
  icon,
  error,
  helperText,
  name,
  value,
  required,
  className,
  placeholder,
  orientation,
  ...props
}: Props) {
  // `name` is for form submission and most callers have none; the label
  // still has to point at the control, so fall back to a generated id.
  const generatedId = useId()
  const id = name ?? generatedId

  return (
    <Field
      className={className}
      data-invalid={!!error}
      orientation={orientation}
    >
      {label && (
        <FieldLabel
          htmlFor={id}
          className={cn("text-nowrap", required && "after:content-['*']")}
        >
          {label}
        </FieldLabel>
      )}
      <InputGroup>
        <InputGroupInput
          id={id}
          name={name}
          value={value ?? ""}
          className={inputClassName}
          placeholder={
            required && placeholder ? `${placeholder} *` : placeholder
          }
          // An unlabelled field still needs an accessible name. The
          // placeholder attribute alone is a last-resort fallback that
          // some screen readers skip and that disappears the moment
          // there's a value, so promote it to the label proper.
          aria-label={label ? undefined : placeholder}
          aria-invalid={!!error}
          {...props}
        />
        {!!icon && <InputGroupAddon align="inline-end">{icon}</InputGroupAddon>}
      </InputGroup>
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
